// Import react
import React, { useState, useEffect } from "react";

// Import states, contexts, and CSS
import { useActiveUser } from "../../store/activeUserContext";
import { SpinnerGeneric } from "../../utils/general/spinners";
import { Card, Form, Row, Col, Button, Table } from "react-bootstrap";
import {
  PlusCircle,
  XCircle,
  ArrowClockwise,
  FunnelFill,
} from "react-bootstrap-icons";
import Pagination from "react-bootstrap/Pagination";

import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Popover from "react-bootstrap/Popover";

import Badge from "react-bootstrap/Badge";
import Stack from "react-bootstrap/Stack";

import styles from "./itemListFilter.module.scss";

// Import components
import ColumnSelector from "./columnSelector";

const ItemListFilterComponent = ({
  filter,
  setFilter,
  page,
  setPage,
  limit,
  setLimit,
  totalPages,
  sort,
  setSort,
  setUpdate,
  isLoading,
  itemList,
  setModalShow,
  columnsToShow,
  setColumnsToShow,
  totalColumns,
  setTotalColumns
}) => {
  const { activeUser } = useActiveUser();
  const [goToPageInput, setGoToPageInput] = useState(page);

  const handleFilterChange = (e) => {
    setFilter(e.target.value);
    setPage(1);
    setGoToPageInput(page);
  };


  useEffect(() => {
    setColumnsToShow(activeUser.itemListColumns.filter((col) => col.show) || []);
    setTotalColumns(activeUser.itemListColumns || []);
  }, [activeUser]);

  const renderPopover = (title, content) => (
    <Popover id={`popover-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <Popover.Header as="h3">{title}</Popover.Header>
      <Popover.Body>{content}</Popover.Body>
    </Popover>
  );

  const handleLimitChange = (e) => {
    setLimit(parseInt(e.target.value));
    setPage(1);
    setGoToPageInput(page);
  };

  const handleRefresh = () => {
    setUpdate((prev) => !prev);
    setPage(1);
    setFilter("");
    setGoToPageInput(page);
  };

  const handlePageChange = (pageNumber) => {
    setPage(pageNumber);
    setGoToPageInput(pageNumber);
  };

  const handleManualPageChange = (e) => {
    const value = parseInt(e.target.value);
    setGoToPageInput(value);
  };

  const handleGoToPage = () => {
    if (goToPageInput >= 1 && goToPageInput <= totalPages) {
      setPage(goToPageInput);
    } else {
      setGoToPageInput(page);
      alert("Numero pagina non valido!");
    }
  };

  return (
    <>
      <Row className="mb-3">
        <Col xs={6} className="d-flex align-items-center">
          <Stack
            direction="horizontal"
            gap={3}
            className="justify-content-center w-100"
          >
            <OverlayTrigger
              trigger="click"
              placement="bottom"
              overlay={renderPopover(
                "Elementi per pagina",
                <>
                  <Form.Select value={limit} onChange={handleLimitChange}>
                    {[10, 20, 50, 100].map((val) => (
                      <option key={val} value={val}>
                        {val}
                      </option>
                    ))}
                  </Form.Select>
                </>
              )}
              rootClose
            >
              <Badge
                pill
                bg="primary"
                className={styles["custom-pill"]}
                role="button"
              >
                Elementi per pagina
              </Badge>
            </OverlayTrigger>

            <OverlayTrigger
              trigger="click"
              placement="bottom"
              overlay={renderPopover(
                "Ordina",
                <>
                  <Form.Select
                    value={sort}
                    onChange={(e) => {
                      setSort(e.target.value);
                      setPage(1); // Resetta alla prima pagina su cambio sort
                      setGoToPageInput(page);
                    }}
                  >
                    <option value="itemName">Item Name</option>
                    <option value="itemCode">Item Code</option>
                    <option value="itemType">Item Type</option>
                    <option value="itemCategory1">Category 1</option>
                    <option value="itemCategory2">Category 2</option>
                    <option value="itemCategory3">Category 3</option>
                    <option value="itemCategory4">Category 4</option>
                    <option value="itemCategory5">Category 5</option>
                    <option value="itemSize">Size</option>
                    <option value="itemBrand">Brand</option>
                    <option value="itemWeight">Weight</option>
                  </Form.Select>
                </>
              )}
              rootClose
            >
              <Badge
                pill
                bg="primary"
                className={styles["custom-pill"]}
                role="button"
              >
                Ordina
              </Badge>
            </OverlayTrigger>

            <OverlayTrigger
              trigger="click"
              placement="bottom"
              overlay={renderPopover(
                "Filtra",
                <>
                  <Form.Group controlId="searchInput" className="mb-3">
                    <Form.Control
                      type="text"
                      placeholder="Search for item..."
                      value={filter}
                      onChange={handleFilterChange}
                      className="me-2" // Margine destro per separare dal pulsante
                    />
                  </Form.Group>
                </>
              )}
              rootClose
            >
              <Badge
                pill
                bg="primary"
                className={styles["custom-pill"]}
                role="button"
              >
                {filter && <FunnelFill size={16} className="me-1" />}
                Filtra
              </Badge>
            </OverlayTrigger>

            <OverlayTrigger
              trigger="click"
              placement="bottom"
              overlay={renderPopover(
                "Colonne",
                <ColumnSelector
                  columns={totalColumns}
                  setColumns={setTotalColumns}
                  setUpdate = {setUpdate}
                />
              )}
              rootClose
            >
              <Badge
                pill
                bg="primary"
                className={styles["custom-pill"]}
                role="button"
              >
                Colonne
              </Badge>
            </OverlayTrigger>

            <Badge
              pill
              bg="success"
              className={styles["custom-pill"]}
              role="button"
              onClick={handleRefresh}
            >
              Aggiorna
            </Badge>

            <Badge
              pill
              bg="secondary"
              className={styles["custom-pill"]}
              onClick={() => setModalShow(true)} // Show modal when clicked
              role="button"
            >
              Aggiungi Articolo
            </Badge>
          </Stack>
        </Col>
        <Col xs={5} className="d-flex align-items-center">
          <Stack
            direction="horizontal"
            gap={3}
            className="justify-content-center w-100"
          >
            {isLoading ? (
              <></>
            ) : (
              itemList.length > 0 && (
                <div className="d-flex justify-content-center mt-3">
                  <Pagination>
                    <Pagination.First
                      onClick={() => handlePageChange(1)}
                      disabled={page === 1}
                    />
                    <Pagination.Prev
                      onClick={() => handlePageChange(page - 1)}
                      disabled={page === 1}
                    />

                    {page > 3 && (
                      <>
                        <Pagination.Item onClick={() => handlePageChange(1)}>
                          1
                        </Pagination.Item>
                        {page > 4 && <Pagination.Ellipsis disabled />}
                      </>
                    )}

                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter((pg) => pg >= page - 2 && pg <= page + 2)
                      .map((pg) => (
                        <Pagination.Item
                          key={pg}
                          active={pg === page}
                          onClick={() => handlePageChange(pg)}
                        >
                          {pg}
                        </Pagination.Item>
                      ))}

                    {page < totalPages - 2 && (
                      <>
                        {page < totalPages - 3 && (
                          <Pagination.Ellipsis disabled />
                        )}
                        <Pagination.Item
                          onClick={() => handlePageChange(totalPages)}
                        >
                          {totalPages}
                        </Pagination.Item>
                      </>
                    )}

                    <Pagination.Next
                      onClick={() => handlePageChange(page + 1)}
                      disabled={page === totalPages}
                    />
                    <Pagination.Last
                      onClick={() => handlePageChange(totalPages)}
                      disabled={page === totalPages}
                    />
                  </Pagination>
                </div>
              )
            )}
          </Stack>
        </Col>
        <Col xs={1} className="d-flex align-items-center">
          <Stack
            direction="horizontal"
            gap={1}
            className="justify-content-center w-100"
          >
            <Form.Control
              type="text"
              name="goToPageInput"
              value={goToPageInput || 0}
              onChange={handleManualPageChange}
            />

            {page !== goToPageInput && (

            <Button variant="warning" onClick={handleGoToPage}>
              <ArrowClockwise size={16} />
            </Button>

            )

            }
          </Stack>
        </Col>
      </Row>
    </>
  );
};

export default ItemListFilterComponent;
